import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import CommentsSection from '../CommentsSection';
import { ReactionButton } from '../ReactionButton';
import { OnlineDot } from '../OnlineIndicator';
import EditPostModal from '../EditPostModal';
import ShareModal from '../ShareModal';
import ProductLinkCard from '../ProductLinkCard';
import {
  Users, UserPlus, Send, Image as ImageIcon, X,
  MessageCircle, Trash2, Share2, FileText,
  Link as LinkIcon, Loader2, Pin, PinOff, MoreHorizontal, Pencil,
  ExternalLink
} from 'lucide-react';
import { API, resolveUrl } from './shared';

/* ---------- Brief audit beta Feb 2026 (P1 §15) — parse [product:id:label]
     and [community:id:label] shortcodes so shared products render as
     interactive cards inside community wall posts, not as inactive text. */
const parseRichContent = (content) => {
  if (!content) return { text: '', products: [], communities: [] };
  const products = [];
  const communities = [];
  let text = content;

  const productRegex = /\[product:([^:]+):([^\]]+)\]/g;
  let match;
  while ((match = productRegex.exec(content)) !== null) {
    products.push({ id: match[1], title: match[2] });
  }
  text = text.replace(productRegex, '').trim();

  const communityRegex = /\[community:([^:]+):([^\]]+)\]/g;
  while ((match = communityRegex.exec(content)) !== null) {
    communities.push({ id: match[1], title: match[2] });
  }
  text = text.replace(communityRegex, '').trim();
  return { text, products, communities };
};

const WallTab = ({ communityId, community, isMember, isAdmin, posts, postsLoading, currentUser, navigate, onPostCreated, onPostDeleted, joinLoading, onToggleMembership, onRefresh }) => {
  const [newPostContent, setNewPostContent] = useState('');
  const [postMediaUrl, setPostMediaUrl] = useState('');
  const [postMediaPreview, setPostMediaPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const [localPosts, setLocalPosts] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [editingPost, setEditingPost] = useState(null);  // Brief Vague 2 §6 — édition F18 dans communautés
  const [sharePost, setSharePost] = useState(null);  // Brief 27 oct — le bouton Partager était inerte
  const fileInputRef = useRef(null);
  // Brief 27 oct LOT C.1 — les posts d'une communauté privée ne se partagent pas à l'extérieur.
  const canShare = (community?.join_mode || 'open') !== 'invitation';

  const displayPosts = localPosts !== null ? localPosts : posts;

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type.startsWith('image/')) {
      setPostMediaPreview({ type: 'image', url: URL.createObjectURL(file), name: file.name });
    } else if (file.type.startsWith('video/')) {
      setPostMediaPreview({ type: 'video', url: URL.createObjectURL(file), name: file.name });
    } else {
      setPostMediaPreview({ type: 'file', name: file.name });
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setPostMediaUrl(response.data.file_url);
    } catch (error) { setPostMediaPreview(null); }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && !postMediaUrl) return;
    setPosting(true);
    try {
      await axios.post(`${API}/communities/${communityId}/posts`, {
        content: newPostContent.trim(), media_url: postMediaUrl || null
      }, { withCredentials: true });
      setNewPostContent(''); setPostMediaUrl(''); setPostMediaPreview(null);
      onPostCreated();
    } catch (error) { console.error(error); }
    finally { setPosting(false); }
  };

  const handleLikePost = async (postId) => {
    const current = (localPosts || posts).find(p => p.post_id === postId);
    const isLiked = current?.liked;
    try {
      if (isLiked) {
        await axios.delete(`${API}/posts/${postId}/like`, { withCredentials: true });
        setLocalPosts(prev => (prev || posts).map(p => p.post_id === postId ? { ...p, like_count: Math.max(0, (p.like_count || 0) - 1), liked: false } : p));
      } else {
        await axios.post(`${API}/posts/${postId}/like`, {}, { withCredentials: true });
        setLocalPosts(prev => (prev || posts).map(p => p.post_id === postId ? { ...p, like_count: (p.like_count || 0) + 1, liked: true } : p));
      }
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Supprimer cette publication ?')) return;
    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      setLocalPosts(prev => (prev || posts).filter(p => p.post_id !== postId));
      onPostDeleted();
    } catch (error) { console.error(error); }
  };

  // Sprint 1.0c V1.b — Pinning (admin only)
  const handleTogglePin = async (post) => {
    try {
      if (post.is_pinned) {
        await axios.delete(`${API}/communities/${communityId}/pinned-post`, { withCredentials: true });
      } else {
        await axios.post(`${API}/communities/${communityId}/posts/${post.post_id}/pin`, {}, { withCredentials: true });
      }
      if (onRefresh) onRefresh();
      else onPostCreated();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Action impossible');
    }
  };

  // Brief Vague 2 §6 — édition de posts dans les communautés (réutilise EditPostModal F18)
  const handleEditPostSaved = (updated) => {
    setLocalPosts(prev => (prev || posts).map(p =>
      p.post_id === updated.post_id ? { ...p, ...updated } : p
    ));
    setEditingPost(null);
  };

  return (
    <div className="space-y-5">
      {isMember && (
        <div className="bg-white rounded-2xl shadow-lg p-5" data-testid="community-post-form">
          <div className="flex gap-3">
            <Avatar className="h-10 w-10 flex-shrink-0"><AvatarImage src={currentUser?.avatar} /><AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">{currentUser?.display_name?.[0]}</AvatarFallback></Avatar>
            <div className="flex-1 space-y-3">
              <textarea value={newPostContent} onChange={(e) => setNewPostContent(e.target.value)} placeholder="Partagez quelque chose..." className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none resize-none text-sm min-h-[80px]" data-testid="community-post-input" />
              {postMediaPreview && (
                <div className="relative">
                  {postMediaPreview.type === 'image' ? (
                    <div className="relative rounded-lg overflow-hidden"><img src={postMediaPreview.url} alt="" className="w-full max-h-[200px] object-cover" /><button onClick={() => { setPostMediaPreview(null); setPostMediaUrl(''); }} className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"><X className="h-4 w-4" /></button></div>
                  ) : postMediaPreview.type === 'video' ? (
                    <div className="relative rounded-lg overflow-hidden"><video src={postMediaPreview.url} controls className="w-full max-h-[200px] rounded-lg" /><button onClick={() => { setPostMediaPreview(null); setPostMediaUrl(''); }} className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"><X className="h-4 w-4" /></button></div>
                  ) : (
                    <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg border"><FileText className="h-5 w-5 text-blue-500" /><span className="text-sm flex-1 truncate">{postMediaPreview.name}</span><button onClick={() => { setPostMediaPreview(null); setPostMediaUrl(''); }} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button></div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept="image/*,video/*,.pdf,.doc,.docx" />
                  <button onClick={() => fileInputRef.current?.click()} className="p-2 text-gray-400 hover:text-purple-500 hover:bg-purple-50 rounded-lg transition-colors" data-testid="community-add-media-btn"><ImageIcon className="h-5 w-5" /></button>
                </div>
                <Button onClick={handleCreatePost} disabled={posting || (!newPostContent.trim() && !postMediaUrl)} size="sm" className="bg-gradient-to-r from-purple-500 to-pink-500 text-white" data-testid="community-post-submit">
                  {posting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}Publier
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {!isMember && (
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <Users className="h-12 w-12 text-purple-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Rejoignez la communaute</h3>
          <p className="text-gray-500 mb-4">Devenez membre pour publier et interagir.</p>
          <Button onClick={onToggleMembership} disabled={joinLoading} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"><UserPlus className="h-4 w-4 mr-2" />Rejoindre</Button>
        </div>
      )}
      {postsLoading ? <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-purple-500" /></div> : displayPosts.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center"><FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune publication</h3><p className="text-gray-500">{isMember ? "Soyez le premier a publier !" : "Les publications apparaitront ici."}</p></div>
      ) : displayPosts.map(post => <WallPostCard key={post.post_id} post={post} onLike={() => handleLikePost(post.post_id)} onDelete={handleDeletePost} onEdit={(p) => setEditingPost(p)} onTogglePin={handleTogglePin} isAdmin={isAdmin} currentUserId={currentUser?.user_id} navigate={navigate} onImageClick={(url) => setLightboxImage(url)} onShare={canShare ? (p) => setSharePost(p) : null} />)}

      {sharePost && (
        <ShareModal
          isOpen={!!sharePost}
          onClose={() => setSharePost(null)}
          shareType="post"
          shareData={{
            id: sharePost.post_id,
            title: `Publication de ${sharePost.author_name || 'OpenSapiens'}`,
            description: (sharePost.content || '').substring(0, 100),
          }}
        />
      )}

      {/* Brief Vague 2 §6 — Modal d'édition partagée avec le Feed/ProfileWall (F18) */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={true}
          onClose={() => setEditingPost(null)}
          onSaved={handleEditPostSaved}
        />
      )}

      {lightboxImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setLightboxImage(null)} data-testid="community-image-lightbox">
          <button onClick={() => setLightboxImage(null)} className="absolute top-4 right-4 p-2 bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors z-10"><X className="h-6 w-6" /></button>
          <img src={lightboxImage} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};


const WallPostCard = ({ post, onLike, onDelete, onEdit, onTogglePin, isAdmin, currentUserId, navigate, onImageClick, onShare }) => {
  const [expandedComments, setExpandedComments] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);  // Brief Vague 2 §6 — menu owner
  const getMediaType = (url) => {
    if (!url) return 'link';
    const l = url.toLowerCase();
    if (l.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)($|\?)/)) return 'image';
    if (l.match(/\.(mp4|webm|mov|avi|mkv)($|\?)/)) return 'video';
    if (l.match(/\.(mp3|wav|ogg|aac|flac|m4a)($|\?)/)) return 'audio';
    if (l.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv)($|\?)/)) return 'document';
    return 'link';
  };
  return (
    <div
      className={`bg-white rounded-2xl shadow-lg overflow-hidden ${post.is_pinned ? 'ring-2 ring-amber-300' : ''}`}
      data-testid={`community-post-${post.post_id}`}
      data-pinned={post.is_pinned ? 'true' : 'false'}
    >
      {post.is_pinned && (
        <div className="flex items-center gap-2 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-200 px-5 py-2" data-testid={`pinned-banner-${post.post_id}`}>
          <Pin className="h-3.5 w-3.5 text-amber-600" />
          <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Publication épinglée</span>
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="relative">
            <Avatar className="h-10 w-10 cursor-pointer" onClick={() => navigate(`/app/profile/${post.user_id}`)}><AvatarImage src={resolveUrl(post.author_avatar)} /><AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">{post.author_name?.[0]}</AvatarFallback></Avatar>
            <OnlineDot userId={post.user_id} className="w-2.5 h-2.5 border" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 text-sm cursor-pointer hover:text-purple-600" onClick={() => navigate(`/app/profile/${post.user_id}`)}>{post.author_name}</p>
            <p className="text-xs text-gray-500">{post.created_at ? new Date(post.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
          </div>
          {/* Pin/unpin (admin) */}
          {isAdmin && onTogglePin && (
            <button
              onClick={() => onTogglePin(post)}
              className={`p-2 rounded-lg ${post.is_pinned ? 'text-amber-600 hover:bg-amber-50' : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50'}`}
              title={post.is_pinned ? 'Désépingler' : 'Épingler ce post (1 max)'}
              data-testid={post.is_pinned ? `unpin-post-${post.post_id}` : `pin-post-${post.post_id}`}
            >
              {post.is_pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            </button>
          )}
          {currentUserId === post.user_id && (
            // Brief Vague 2 §6 — Menu ... (Modifier / Supprimer) sur posts owner
            <div className="relative">
              <button
                onClick={() => setMenuOpen(v => !v)}
                onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
                data-testid={`post-menu-toggle-${post.post_id}`}
                aria-label="Options"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-10 min-w-[140px]"
                  data-testid={`post-menu-${post.post_id}`}
                >
                  <button
                    onMouseDown={(e) => { e.preventDefault(); setMenuOpen(false); onEdit && onEdit(post); }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                    data-testid={`edit-post-${post.post_id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Modifier
                  </button>
                  <button
                    onMouseDown={(e) => { e.preventDefault(); setMenuOpen(false); onDelete(post.post_id); }}
                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                    data-testid={`delete-post-${post.post_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {post.content && (() => {
          const parsed = parseRichContent(post.content);
          return (
            <>
              {parsed.text && <p className="text-gray-800 whitespace-pre-line text-sm leading-relaxed mb-3">{parsed.text}</p>}
              {parsed.products.map((p) => <ProductLinkCard key={`p-${p.id}`} productId={p.id} title={p.title} testId={`wall-product-share-${p.id}`} />)}
              {parsed.communities.map((c) => (
                <button
                  key={`c-${c.id}`}
                  type="button"
                  onClick={() => navigate && navigate(`/app/community/${c.id}`)}
                  className="mt-2 mb-2 w-full flex items-center gap-3 p-3 rounded-xl border border-cyan-100 bg-cyan-50 hover:bg-cyan-100 transition-colors text-left"
                  data-testid={`wall-community-share-${c.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0">
                    <Users className="h-5 w-5 text-cyan-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-cyan-500">Communauté partagée</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{c.title}</p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-cyan-400 shrink-0" />
                </button>
              ))}
            </>
          );
        })()}
        {post.media_url && (() => {
          const type = getMediaType(post.media_url);
          const url = resolveUrl(post.media_url);
          if (type === 'image') return <img src={url} alt="" className="w-full max-h-[400px] object-contain bg-gray-50 rounded-xl cursor-pointer hover:opacity-95 transition-opacity" onClick={() => onImageClick && onImageClick(url)} />;
          if (type === 'video') return <video src={url} controls playsInline preload="metadata" className="w-full max-h-[400px] rounded-xl bg-black" />;
          if (type === 'audio') return <div className="bg-gray-50 p-3 rounded-xl"><audio src={url} controls className="w-full" /></div>;
          return <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-purple-50 p-3 rounded-xl hover:bg-purple-100 border border-purple-100"><LinkIcon className="h-5 w-5 text-purple-500" /><span className="text-sm text-purple-700 truncate">{post.media_url}</span></a>;
        })()}
      </div>
      <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-6">
        <ReactionButton postId={post.post_id} reactions={post.reactions || {}} userReaction={post.user_reaction} onReactionChange={onLike} size="sm" />
        <button onClick={() => setExpandedComments(!expandedComments)} className={`flex items-center gap-1.5 text-sm ${expandedComments ? 'text-purple-600' : 'text-gray-500 hover:text-purple-600'}`} data-testid={`comment-btn-${post.post_id}`}><MessageCircle className={`h-4 w-4 ${expandedComments ? 'fill-purple-100' : ''}`} />{post.comment_count || 0}</button>
        {onShare && (
          <button onClick={() => onShare(post)} className="text-gray-500 hover:text-purple-500 ml-auto flex items-center gap-1.5 text-sm" data-testid={`share-btn-${post.post_id}`} aria-label="Partager"><Share2 className="h-4 w-4" /><span className="hidden sm:inline">Partager</span></button>
        )}
      </div>
      {expandedComments && <div className="px-5 pb-5 pt-1"><CommentsSection postId={post.post_id} initialCount={post.comment_count || 0} /></div>}
    </div>
  );
};

export default WallTab;
